import VectorSource from 'ol/source/Vector.js'
import { bbox as bboxStrategy } from 'ol/loadingstrategy.js'
import store from '@/store.js'

const PAGE_SIZE = 1000

function reportLoadState(layerId, loading, progress) {
  store.dispatch('setWmsLayerLoadState', { layerId, loading, progress })
}

function toastLoadError(layerName) {
  store.dispatch('toast/show', {
    message: `No se pudieron cargar los features de "${layerName}" — verifica la URL del servicio o los permisos CORS.`,
    type: 'error'
  })
}

/**
 * Carga paginada: pide de a PAGE_SIZE features hasta completar `total` (conocido de antemano vía
 * getTotalCount). El % de progreso es features recibidos / total — no depende de bytes ni de
 * compresión, a diferencia del progreso por Content-Length.
 */
async function loadPaginated({ source, format, projection, extent, layerId, total, fetchPage, signal }) {
  let offset = 0
  let received = 0
  const maxPages = Math.ceil(total / PAGE_SIZE) + 3 // margen ante servidores que ignoran offset/count

  for (let page = 0; page < maxPages && received < total; page++) {
    const rawFeatures = await fetchPage({ extent, projection, offset, pageSize: PAGE_SIZE, signal })
    if (!rawFeatures.length) break // corte de seguridad: evita loop infinito si el servidor no avanza

    const features = format.readFeatures(
      { type: 'FeatureCollection', features: rawFeatures },
      { featureProjection: projection }
    )
    source.addFeatures(features)

    received += rawFeatures.length
    offset += rawFeatures.length
    reportLoadState(layerId, true, Math.min(1, received / total))
  }
}

/**
 * Fallback cuando no se pudo determinar el total de features (servidor sin soporte de conteo):
 * una sola descarga completa, con % por Content-Length si el servidor lo informa, o barra
 * indeterminada si no (chunked encoding).
 */
async function loadSingleShot({ source, format, projection, layerId, layerName, url, signal }) {
  const res = await fetch(url, { signal })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)

  const total = Number(res.headers.get('content-length')) || 0
  const reader = res.body?.getReader()
  const chunks = []
  let received = 0

  if (reader) {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      received += value.length
      reportLoadState(layerId, true, total ? Math.min(1, received / total) : null)
    }
  }

  const bytes = reader ? new Uint8Array(received) : new Uint8Array(await res.arrayBuffer())
  if (reader) {
    let offset = 0
    for (const chunk of chunks) {
      bytes.set(chunk, offset)
      offset += chunk.length
    }
  }

  const text = new TextDecoder('utf-8').decode(bytes)
  const features = format.readFeatures(text, { featureProjection: projection })
  source.addFeatures(features)
}

/**
 * VectorSource con carga por extensión visible (strategy: bbox, mismo patrón que
 * layers.js:229-258 y layerService.js:380-423 para ArcGIS del catálogo) y loader propio
 * (addFeatures + success, igual que layerService.js) que reporta progreso a store.wmsSessionLayers.
 *
 * - getTotalCount({ extent, projection }): async () => number|null. Conteo de features dentro de esa
 *   extensión. Si devuelve un total válido, se pagina con fetchPage() y el progreso es features
 *   recibidos / total de esa extensión (confiable, no depende de bytes).
 * - fetchPage({ extent, projection, offset, pageSize }): async () => Feature[] (GeoJSON crudo).
 * - buildSingleShotUrl({ extent, projection }): string — URL de descarga completa de esa extensión,
 *   usada como fallback si no hay total.
 */
export function createProgressVectorSource({ layerId, layerName, format, getTotalCount, fetchPage, buildSingleShotUrl }) {
  const abortController = new AbortController()

  const source = new VectorSource({
    format,
    strategy: bboxStrategy,
    loader: async (extent, resolution, projection, success, failure) => {
      const signal = abortController.signal
      reportLoadState(layerId, true, null)
      try {
        const total = await getTotalCount({ extent, projection, signal }).catch(() => null)

        if (total != null && total > 0) {
          await loadPaginated({ source, format, projection, extent, layerId, total, fetchPage, signal })
        } else {
          await loadSingleShot({ source, format, projection, layerId, layerName, url: buildSingleShotUrl({ extent, projection }), signal })
        }

        success([])
      } catch (error) {
        source.removeLoadedExtent(extent)
        failure()
        if (error.name === 'AbortError') {
          // Cancelación intencional (ej. la capa se eliminó mid-carga) — no es un error real.
          console.log(`Carga cancelada: "${layerName}"`)
        } else {
          console.error(`No se pudieron cargar los features de "${layerName}":`, error)
          toastLoadError(layerName)
        }
      } finally {
        reportLoadState(layerId, false, null)
      }
    }
  })

  source.set('abortController', abortController)
  return source
}
